<template>
  <div class="layout-pods package-list orders">
    <header class="layout-pods-header pg-header">
      <div>
        <tab-buttons
          v-show="$isAllowed('edit')"
          ref="tabs"
          @changeTab="changeTab"
          :tabs="[
            {name: 'My Packages', isActive: true},
            {name: 'Archived', isActive: false},
          ]"
        >
        </tab-buttons>
        <button class="btn btn-primary ml-4" @click="openModal(null)">
          Create Package
        </button>
        <button class="btn btn-quaternary ml-4" @click="exportPackages()" v-if="$isAllowed('exportPackages')">
          Export Packages
        </button>
      </div>
      <div class="agencies-filter">
        <div class="">
          <client-list-dropdown
            :value="selectedClient"
            @on-client-selected="handleClientSelected"
            @on-client-removed="handleClientRemoved"
            :style="{width: '300px'}"
          />
        </div>
        <form
          id="client-search"
          class="search-form campaigns-client-search ml-4"
        >
          <input
            class="form-control"
            type="search"
            placeholder="Search"
            aria-label="Search"
            v-model="searchText"
            :disabled="selectedClient === null"
          />
        </form>
      </div>
    </header>
    <app-action-needed
      v-if="selectedClient === null"
      title="No client selected"
      description="Please select a client to get started."
    />
    <app-no-data-available
      v-if="selectedClient && packages.length === 0"
      :title="`No data available for ${selectedClient.title}`"
      description="There aren't any packages available matching the criteria."
    />
    <div class="pods-container">
      <section class="notifications-list">
        <template>
          <ol v-if="packages.length > 0">
            <li>
              <div class="container-fluid">
                <div class="row">
                  <div class="col-4">
                    <b>Package Name</b>
                  </div>
                  <div class="col-3">
                    <b>Package Type</b>
                  </div>
                  <div class="col-1">
                    <b>Retail Price</b>
                  </div>
                  <div class="col-1">
                    <b>Package Price</b>
                  </div>
                  <div class="col-1">
                    <b>Property Duration (days)</b>
                  </div>
                  <div class="col-1">
                    <b>Agent Duration (days)</b>
                  </div>
                  <div class="col-1">
                    <b>Office Duration (days)</b>
                  </div>
                </div>
              </div>
            </li>
            <li v-for="_package in packages" :key="_package.id">
              <div class="container-fluid">
                <div class="row" style="padding:10px">
                  <div class="col-4" @click.stop.prevent="openModal(_package)">
                    <a href="">{{ _package.title }}</a>
                  </div>
                  <div class="col-3 vertical-align-middle">
                    <span v-if="_package.type">{{
                      _package.type | capitalizeTheFirstLetterOfEachWord
                    }}</span>
                  </div>
                  <div class="col-1 vertical-align-middle">
                    ${{ _package.retailPrice }}
                  </div>
                  <div class="col-1 vertical-align-middle">
                    ${{ _package.packagePrice }}
                  </div>
                  <div class="col-1 vertical-align-middle">
                    {{ _package.propertyDuration }}
                  </div>
                  <div class="col-1 vertical-align-middle">
                    {{ _package.agentDuration }}
                  </div>
                  <div class="col-1 vertical-align-middle">
                    {{ _package.officeDuration }}
                  </div>
                </div>
              </div>
            </li>
          </ol>
        </template>
      </section>
    </div>

    <div class="col-12 mt-4" v-if="packages.length > 0 && hasMorePages">
      <div class="btn-container" style="text-align: center">
        <button class="btn-primary btn" @click="currentCursor = nextCursor">
          Load more
        </button>
      </div>
    </div>
    <transition name="modal">
      <package-modal
        v-if="showModal"
        :package-id="packageId"
        @closeModal="closeModal"
      ></package-modal>
    </transition>

    <app-modal-progress-bar
      v-if="showExportModal"
      :progress="exportProgress"
      label="Exporting packages..."
      :state="exportModalState"
    ></app-modal-progress-bar>
  </div>
</template>

<script>
import PackagePerimeters from '../../configs/Perimeters/packages';
import TabButtons from '../../helpers/TabButtons';
import PackageModal from './PackageModal';
import PackageServices from '../../services/packages';
import {mapGetters} from 'vuex';
import {refreshPageMixin} from '../../mixins/refreshPage';
import ClientListDropdown, {
  ClientListAllOption,
} from '../Clients/ClientListDropdown.vue';
import AppActionNeeded from '../AppComponents/AppActionNeeded.vue';
import AppNoDataAvailable from '../AppComponents/AppNoDataAvailable.vue';
import {FilterBuilder} from '../../helpers/FilterBuilder';
import PackageServiceV2 from '../../services/V2/PackageServiceV2';
import AppModalProgressBar from '../AppComponents/Loaders/AppModalProgressBar.vue';

export default {
  mixins: [refreshPageMixin],
  perimeters: [PackagePerimeters],
  components: {
    TabButtons,
    PackageModal,
    ClientListDropdown,
    AppActionNeeded,
    AppNoDataAvailable,
    AppModalProgressBar,
  },
  data() {
    return {
      showModal: false,
      packageId: false,
      packages: [],
      searchText: '',
      showClients: false,
      currentTabName: null,
      selectedClient: null,
      searchTimeout: null,
      filterBuilder: new FilterBuilder(),
      status: 'published',
      currentCursor: 0,
      nextCursor: 0,
      hasMorePages: true,
      exportModalState: 'loading',
      showExportModal: false,
      exportProgress: 0,
    };
  },
  computed: {
    ...mapGetters(['getPackagesFilter']),
  },
  methods: {
    closeModal() {
      this.showModal = false;
      this.packageId = null;
      if (null !== this.selectedClient) {
        this.getPackages();
      }
    },
    openModal(_package) {
      if (_package && _package.status !== 'archived') {
        this.showModal = true;
        this.packageId = _package.id;
      } else if (_package === null) {
        this.showModal = true;
      }
    },
    getPackages() {
      PackageServices.getPackages(
        this.status,
        '',
        this.filterBuilder.get(),
        this.currentCursor
      ).then((response) => {
        // Append if the user is navigating to the next page.
        this.packages =
          this.currentCursor === 0
            ? response.data
            : [...this.packages, ...response.data];
        const hasNext = response.meta.cursor.hasOwnProperty('next');
        this.hasMorePages = hasNext;
        if (hasNext) {
          this.nextCursor = response.meta.cursor.next.cursor;
        }
      });
    },
    changeTab(tab) {
      this.currentTabName = tab.name;
      this.status = tab.name === 'Archived' ? 'archived' : 'published';

      if (this.selectedClient === null) return;

      this.currentCursor = 0;
      this.getPackages();
    },
    unArchive(product) {
      product.status = 'published';
      PackageServices.updatePackage(product)
        .then((product) => {
          this.getPackages();
          this.$refs.tabs.changeTab({name: 'My Packages'});
        })
        .catch((errors) => {
          this.errors = errors;
        });
    },
    handleClientSelected(client) {
      this.currentCursor = 0;
      this.selectedClient = client;
    },
    handleClientRemoved(client) {
      this.currentCursor = 0;
      this.selectedClient = null;
      this.packages = [];
    },
    async exportPackages() {
      try {
        this.exportModalState = 'loading';
        this.showExportModal = true;
        this.exportProgress = 0;

        let interval = setInterval(() => {
          if (this.exportProgress < 90) {
            this.exportProgress = this.exportProgress + 1;
          }
        }, 250);


        const response = await PackageServiceV2.exportPackages();
        this.exportProgress = 100;
        if (response.data.size > 0) {
          let url = window.URL.createObjectURL(new Blob([response.data]));
          let link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Packages Export.csv`);
          document.body.appendChild(link);
          link.click();
        }
        clearInterval(interval);
      } catch (error) {
      } finally {
        this.exportModalState = 'loaded';
        this.exportProgress = 100;
        setTimeout(() => {
          this.showExportModal = false;
        }, 3000);
      }
    },
  },
  watch: {
    searchText: {
      handler: function(value) {
        if (this.selectedClient === null) return;

        clearTimeout(this.searchTimeout);

        if (!value.length) {
          this.filterBuilder.removeFilter('searchText');
        } else {
          this.filterBuilder.addFilter('searchText', value);
        }

        this.searchTimeout = setTimeout(() => {
          this.currentCursor = 0;
          this.getPackages();
        }, 1000);
      },
      immediate: true,
    },
    selectedClient: {
      handler: function(value) {
        // Don't do anything if there's no client selected
        if (!value) return;
        if (this.selectedClient.length === 0) {
          return;
        } else if (
          // If the selected client is "all clients"
          this.selectedClient.id === ClientListAllOption.id
        ) {
          this.filterBuilder.removeFilter('client');
        } else {
          // Else filter according to client ID
          this.filterBuilder.addFilter('client', [this.selectedClient.id]);
        }
        this.getPackages();
      },
    },
    currentCursor: {
      handler(value) {
        if (value === 0) return;
        this.getPackages();
      },
    },
  },
  beforeDestroy() {
    this.filterBuilder = null;
  },
};
</script>

<style lang="css"></style>
